import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/lib/adminApi';
import { unmatchedCounts, visibleUnmatched, type UnmatchedBiomarker } from '@/lib/unmatched';
import { AdminPage, RegistryTable } from '@/admin';
import { EmptyNote } from '@/admin/states';
import { DomainPill } from '@/components/DomainPill';
import { Button } from '@/components/ui/button';

const STATUS_LABEL: Record<string, string> = { auto_mapped: 'Auto-mapped', mapped: 'Approved', dismissed: 'Dismissed' };

function errText(e: unknown): string {
  return e instanceof Error ? e.message : 'Error';
}

/**
 * The unmatched-biomarker review queue (legacy /review, rebuilt here; owner 2026-10-19).
 * Names read from a person's documents that no canonical biomarker matched: approve the
 * suggested mapping (an alias for every user) or dismiss it. Admin only, as at core.
 */
export function UnmatchedBiomarkersPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [userRef, setUserRef] = useState('');
  const [history, setHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const testers = useQuery({ queryKey: ['testers'], queryFn: () => adminApi.testers.list() });
  const key = ['unmatched', userRef, history];
  const q = useQuery({ queryKey: key, queryFn: () => adminApi.unmatched.list(userRef, history), enabled: userRef !== '' });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['unmatched', userRef] });

  const approve = useMutation({
    mutationFn: (i: UnmatchedBiomarker) => adminApi.unmatched.approve(i.id, i.suggested_canonical ?? ''),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: unknown) => { setError(errText(e)); refresh(); },
  });
  const dismiss = useMutation({
    mutationFn: (i: UnmatchedBiomarker) => adminApi.unmatched.dismiss(i.id),
    onSuccess: () => { setError(null); refresh(); },
    onError: (e: unknown) => { setError(errText(e)); refresh(); },
  });

  const items = q.data ?? [];
  const counts = unmatchedCounts(items);
  const rows = visibleUnmatched(items, history);
  const busy = approve.isPending || dismiss.isPending;

  return (
    <AdminPage
      title="Unmatched biomarkers"
      description="Biomarker names that couldn't be automatically matched to canonical names. Review each suggestion and approve or dismiss. Approving adds the name as an alias for every user."
      actions={userRef ? (
        <Button data-testid="unmatched-history" variant="outline" size="sm" onClick={() => setHistory((h) => !h)}>
          {history ? 'Hide history' : 'Show history'}
        </Button>
      ) : null}
    >
      <form className="mb-13 flex flex-wrap items-center gap-8"
        onSubmit={(e) => { e.preventDefault(); setError(null); setUserRef(draft.trim()); }}>
        <input
          data-testid="unmatched-user" list="unmatched-users" value={draft} onChange={(e) => setDraft(e.target.value)}
          placeholder="User ref…"
          className="w-[260px] rounded-sm border border-border-strong px-2.5 py-2 text-body-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        />
        <datalist id="unmatched-users">
          {(testers.data ?? []).map((t) => <option key={t.user_ref} value={t.user_ref} />)}
        </datalist>
        <Button data-testid="unmatched-load" type="submit" variant="outline" size="sm" disabled={!draft.trim()}>Load queue</Button>
      </form>

      {error ? <p data-testid="unmatched-error" className="mb-8 text-body-sm text-flagged">{error}</p> : null}

      {!userRef ? (
        <EmptyNote>Enter a user ref to load their queue.</EmptyNote>
      ) : (
        <>
          {q.isSuccess ? (
            <p data-testid="unmatched-counts" className="mb-8 text-body-sm text-text-secondary">
              {counts.pending} pending · {counts.resolved} resolved · {counts.dismissed} dismissed
              {history ? '' : ' (history hidden)'}
            </p>
          ) : null}
          <RegistryTable<UnmatchedBiomarker>
            columns={[
              { key: 'found', header: 'Found name', className: 'font-medium', render: (i) => i.found_name },
              { key: 'domain', header: 'Domain', render: (i) => <DomainPill domain={i.domain} /> },
              { key: 'suggested', header: 'Suggested', className: 'font-mono text-[12px]', render: (i) => i.suggested_canonical || '—' },
              { key: 'confidence', header: 'Confidence', className: 'text-[12px]',
                render: (i) => (i.confidence == null ? '—' : `${Math.round(i.confidence * 100)}%`) },
              { key: 'reasoning', header: 'Why', className: 'text-[12px] text-text-secondary', render: (i) => i.reasoning || '—' },
              { key: 'status', header: 'Status', className: 'text-[12px]',
                render: (i) => <span data-testid={`unmatched-status-${i.id}`}>{i.status === 'pending' ? 'Pending' : STATUS_LABEL[i.status] ?? i.status}</span> },
            ]}
            rows={rows}
            getRowKey={(i) => String(i.id)}
            rowActions={(i) => (i.status !== 'pending' ? null : (
              <div className="flex items-center gap-2">
                {i.suggested_canonical ? (
                  <Button data-testid={`unmatched-approve-${i.id}`} variant="outline" size="sm" disabled={busy}
                    onClick={() => approve.mutate(i)}>Approve mapping</Button>
                ) : null}
                <Button data-testid={`unmatched-dismiss-${i.id}`} variant="outline" size="sm" disabled={busy}
                  onClick={() => dismiss.mutate(i)}>Dismiss</Button>
              </div>
            ))}
            state={{ loading: q.isLoading, error: q.isError }}
            count={rows.length}
            emptyText={history ? 'No unmatched biomarkers for this user.' : 'All clear: no biomarkers pending review.'}
          />
        </>
      )}
    </AdminPage>
  );
}
